import { isEmpty } from 'lodash';
import { useCallback, useContext } from 'react';
import { NativeSyntheticEvent, StyleProp, TextInputFocusEventData, TextStyle } from 'react-native';
import { View } from '@shared/components/View';
import { MBTextBody2 } from '@shared/components/typography';
import { ColorThemeContext, grid } from '@shared/styles';
import { useBoolean } from '@shared/util/hooks';
import { MBTextInput, MBTextInputProps } from './MBTextInput';
import { MBAutosizeTextInput } from './autosize';

interface Props extends MBTextInputProps {
  error?: string;
  autosize?: boolean;
}

export function TextInput({
  editable = true,
  autosize,
  onFocus,
  onBlur,
  error,
  style,
  ...props
}: Props) {
  const colors = useContext(ColorThemeContext);
  const [isFocused, focus, blur] = useBoolean(props.autoFocus);

  const hasError = !isEmpty(error);

  const baseStyle: TextStyle = {
    padding: grid(1),
    borderRadius: grid(1),
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.input.borderInactive,
    backgroundColor: colors.backgroundInactive,
    color: colors.input.foregroundInactive,
  };

  const editableStyle: TextStyle = {
    color: colors.input.foregroundActive,
    borderColor: isFocused ? colors.input.borderActive : colors.input.borderInactive,
  };

  const errorStyle: TextStyle = {
    borderColor: colors.error.border,
    color: colors.error.foreground,
  };

  const inputStyle: StyleProp<TextStyle> = [
    baseStyle,
    style,
    editable && editableStyle,
    hasError && errorStyle,
  ];

  const focusHandler = useCallback(
    (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
      focus();
      onFocus?.(event);
    },
    [focus, onFocus],
  );

  const blurHandler = useCallback(
    (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
      blur();
      onBlur?.(event);
    },
    [blur, onBlur],
  );

  const inputProps = {
    ...props,
    onFocus: focusHandler,
    onBlur: blurHandler,
    style: inputStyle,
  };

  return (
    <View>
      {autosize && <MBAutosizeTextInput {...inputProps} />}
      {!autosize && <MBTextInput {...inputProps} editable={editable} />}
      {hasError && (
        <MBTextBody2 color={colors.error.foreground} style={{ marginTop: grid(1) }}>
          {error}
        </MBTextBody2>
      )}
    </View>
  );
}
